import { useState, useEffect } from 'react';
import { X, Package, Layers, Sparkles, Loader2 } from 'lucide-react';
import type { SmartMergeResult } from '../../lib/svgMerge';

type ImportMode = 'group' | 'decompose' | 'smart-merge';

interface SvgImportDialogProps {
  fileName: string;
  elementCount: number;
  onImportDecomposed: () => void;
  onImportGrouped: () => void;
  onImportSmartMerge: () => void;
  onCancel: () => void;
  analyzeSmartMerge: () => Promise<SmartMergeResult | null>;
}

export function SvgImportDialog({
  fileName,
  elementCount,
  onImportDecomposed,
  onImportGrouped,
  onImportSmartMerge,
  onCancel,
  analyzeSmartMerge,
}: SvgImportDialogProps) {
  const [selected, setSelected] = useState<ImportMode>('smart-merge');
  const [analysis, setAnalysis] = useState<SmartMergeResult | null>(null);
  const [analyzing, setAnalyzing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setAnalyzing(true);
    analyzeSmartMerge().then((result) => {
      if (!cancelled) {
        setAnalysis(result);
        setAnalyzing(false);
      }
    });
    return () => { cancelled = true; };
  }, [analyzeSmartMerge]);

  const handleImport = () => {
    if (selected === 'group') onImportGrouped();
    else if (selected === 'decompose') onImportDecomposed();
    else onImportSmartMerge();
  };

  const mergedCount = analysis?.mergedCount ?? '...';
  const reduction = analysis
    ? Math.round(((analysis.originalCount - analysis.mergedCount) / analysis.originalCount) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-[560px] animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700/50">
          <div>
            <h2 className="text-white font-semibold text-sm">Import SVG</h2>
            <p className="text-zinc-500 text-xs mt-0.5">
              <span className="text-zinc-300">{fileName}</span>
              {' '}&middot; {elementCount} element{elementCount !== 1 ? 's' : ''} detected
              {analysis ? (
                <>
                  {' '}&middot;{' '}
                  <span className="text-zinc-400 tabular-nums">
                    {analysis.strokedCount} stroked
                  </span>
                  {' / '}
                  <span className="text-zinc-400 tabular-nums">
                    {analysis.unstrokedCount} unstroked
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-zinc-400 hover:text-white transition-colors p-1 rounded hover:bg-white/5"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <ModeCard
              icon={Package}
              title="As Group"
              subtitle="1 element"
              description="No individual editing"
              selected={selected === 'group'}
              onSelect={() => setSelected('group')}
            />
            <ModeCard
              icon={Layers}
              title="Decompose"
              subtitle={`${elementCount} elements`}
              description="Full individual editing. May be slow with many items."
              selected={selected === 'decompose'}
              onSelect={() => setSelected('decompose')}
            />
            <ModeCard
              icon={Sparkles}
              title="Smart Merge"
              subtitle={analyzing ? '...' : `~${mergedCount} elements`}
              description="Similar items auto-grouped. Best for most design files."
              selected={selected === 'smart-merge'}
              onSelect={() => setSelected('smart-merge')}
              badge="Recommended"
              loading={analyzing}
            />
          </div>

          {selected === 'smart-merge' && (
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 animate-fade-in">
              <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                Smart Merge Preview
              </h4>
              {analyzing ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 size={14} className="text-cyan-400 animate-spin" />
                  <span className="text-xs text-zinc-500">Analyzing element similarity...</span>
                </div>
              ) : analysis ? (
                <div className="space-y-1">
                  {analysis.mergedGroups.slice(0, 6).map((group, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400 truncate max-w-[280px]">{group.suggestedName}</span>
                      <span className="text-zinc-600 tabular-nums shrink-0 ml-2">
                        {group.count} &rarr; 1
                      </span>
                    </div>
                  ))}
                  {analysis.mergedGroups.length > 6 && (
                    <div className="text-[11px] text-zinc-600">
                      + {analysis.mergedGroups.length - 6} more merge groups
                    </div>
                  )}
                  {analysis.individuals.length > 0 && (
                    <div className="text-xs text-zinc-500 mt-1">
                      + {analysis.individuals.length} individual element{analysis.individuals.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  <div className="border-t border-white/[0.06] mt-2 pt-2 flex items-center justify-between">
                    <span className="text-xs text-zinc-400">
                      {analysis.originalCount} &rarr; {analysis.mergedCount} elements
                    </span>
                    <span className="text-xs font-medium text-cyan-400">
                      {reduction}% reduction
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-zinc-600">Could not analyze this SVG.</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-700/50">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-xs text-zinc-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={selected === 'smart-merge' && analyzing}
            className="px-4 py-1.5 text-xs font-medium text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeCard({
  icon: Icon,
  title,
  subtitle,
  description,
  selected,
  onSelect,
  badge,
  loading,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  badge?: string;
  loading?: boolean;
}) {
  return (
    <button
      onClick={onSelect}
      className={`
        relative flex flex-col items-start p-3 rounded-lg border text-left transition-all
        ${selected
          ? 'bg-cyan-500/[0.08] border-cyan-500/50'
          : 'bg-white/[0.02] border-white/[0.08] hover:border-white/20 hover:bg-white/[0.04]'
        }
      `}
    >
      {badge && (
        <span className="absolute -top-2 right-2 text-[9px] font-semibold text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-full border border-cyan-500/20">
          {badge}
        </span>
      )}
      <div className={`p-1.5 rounded-md mb-2 ${selected ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-zinc-500'}`}>
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
      </div>
      <div className={`text-xs font-medium ${selected ? 'text-white' : 'text-zinc-300'}`}>{title}</div>
      <div className={`text-[11px] mt-0.5 ${selected ? 'text-cyan-400' : 'text-zinc-500'}`}>{subtitle}</div>
      <div className="text-[10px] text-zinc-600 mt-1.5 leading-relaxed">{description}</div>
    </button>
  );
}
